import React, { useState, useEffect, useCallback, useRef } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { useLanguage, T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const renderLine = (line, i) => {
  const clean = line.replace(/\*\*/g, "");
  if (line.startsWith("### ")) return <h4 key={i} className="mt-4 text-base font-bold text-slate-900">{clean.slice(4)}</h4>;
  if (line.startsWith("## ")) return <h3 key={i} className="mt-5 text-lg font-bold text-slate-900">{clean.slice(3)}</h3>;
  if (line.startsWith("# ")) return <h3 key={i} className="mt-5 text-lg font-bold text-slate-900">{clean.slice(2)}</h3>;
  if (line.startsWith("- ") || line.startsWith("• ")) return <li key={i} className="ml-5 list-disc text-sm text-slate-700 leading-relaxed">{clean.slice(2)}</li>;
  if (!line.trim()) return null;
  if (line.startsWith("—")) return <p key={i} className="mt-4 text-xs italic text-slate-500">{clean}</p>;
  return <p key={i} className="mt-2 text-sm text-slate-700 leading-relaxed">{clean}</p>;
};

export const AIContent = ({ topic, context = "" }) => {
  const { langName } = useLanguage();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  const generate = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setContent("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/ai/content`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic, context, language: langName }),
        signal: controller.signal,
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) setContent((c) => c + obj.delta);
          } catch {}
        }
      }
    } catch {}
    setLoading(false);
  }, [topic, context, langName]);

  useEffect(() => {
    generate();
    return () => abortRef.current?.abort();
  }, [generate]);

  return (
    <section className="rounded-2xl border border-blue-100 bg-gradient-to-br from-[#EBF2FF] to-white p-6 sm:p-8" data-testid="ai-content-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#0055FF] px-3 py-1 text-xs font-bold text-white">
          <Sparkles className="h-3.5 w-3.5" /> <T>AI-Generated Insight</T>
        </div>
        <button
          data-testid="ai-regenerate-button"
          onClick={generate}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#0055FF] hover:text-[#0055FF] disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> <T>Regenerate</T>
        </button>
      </div>
      <div className="mt-4 min-h-[80px]" data-testid="ai-content-body">
        {content ? (
          content.split("\n").map(renderLine)
        ) : (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span className="h-2 w-2 animate-pulse rounded-full bg-[#0055FF]" />
            <T>SportsPulse AI is writing this section…</T>
          </div>
        )}
      </div>
    </section>
  );
};
